import styles from './mailing-lists.module.css'

import { LuDownload } from '@react-icons/all-files/lu/LuDownload'
import { LuEllipsis } from '@react-icons/all-files/lu/LuEllipsis'
import { LuFileUp } from '@react-icons/all-files/lu/LuFileUp'
import { LuPencil } from '@react-icons/all-files/lu/LuPencil'
import { LuTrash2 } from '@react-icons/all-files/lu/LuTrash2'
import { LuUserPlus } from '@react-icons/all-files/lu/LuUserPlus'
import { LuUserRoundPlus } from '@react-icons/all-files/lu/LuUserRoundPlus'
import type {
  ColumnDef,
  RowSelectionState,
  SortingState,
} from '@tanstack/react-table'
import orderBy from 'lodash/orderBy'
import React, { useCallback, useMemo, useState } from 'react'
import { Link, Outlet, useNavigate } from 'react-router-dom'

import type { BulkDeleteResult } from '@disco/state-management/actions/EmailGroupActions'
import {
  bulkDeleteEmailGroups,
  exportEmailGroup,
  loadMailingListsNextPage,
} from '@disco/state-management/actions/EmailGroupActions'
import { getUser } from '@disco/state-management/selectors/user'
import dayjs from '@disco/utils/dayjs'

import type { MailingList, RecipientListId } from 'types/list'

import EmptyMessage from 'components/common/empty-message'
import DocumentHeadTags from 'components/document-head-tags'
import {
  AlertDialog,
  Button as DsButton,
  DataTable,
  Flex,
  SearchTextField,
  Text,
} from 'components/ds'
import type { RowAction } from 'components/ds/DataTable'
import DownloadModal from 'components/modals/download-modal'
import Button from 'components/ui/button'
import PageContainer from 'components/ui/page-container'
import PageHeader from 'components/ui/page-header'

import { useDispatch, useSelector } from 'hooks/use-redux'

import {
  CAPTION_EXISTING,
  CAPTION_EXPORT,
  CAPTION_IMPORT,
  CAPTION_SINGLE,
} from '../add/buttons'
import IconEmptyLists from './icon-empty-lists'
import SelectionActionMenu from './selection-action-menu'

const DEFAULT_SORTING: SortingState = [{ id: 'created', desc: true }]

/** Wraps every occurrence of `query` in `text` in a highlight mark. */
const highlightMatch = (text: string, query: string): React.ReactNode => {
  if (!query) return text
  const lowerText = text.toLowerCase()
  const lowerQuery = query.toLowerCase()
  const parts: React.ReactNode[] = []
  let cursor = 0

  let matchIndex = lowerText.indexOf(lowerQuery, cursor)
  while (matchIndex !== -1) {
    if (matchIndex > cursor) parts.push(text.slice(cursor, matchIndex))
    parts.push(
      <mark className={styles.Highlight} key={`${matchIndex}`}>
        {text.slice(matchIndex, matchIndex + query.length)}
      </mark>,
    )
    cursor = matchIndex + query.length
    matchIndex = lowerText.indexOf(lowerQuery, cursor)
  }
  if (cursor < text.length) parts.push(text.slice(cursor))

  return parts
}

const SORT_ITERATEES: Record<string, (_list: MailingList) => string | number> =
  {
    name: list => (list.name ?? '').toLowerCase(),
    count_recipients: list => list.count_recipients ?? 0,
    created: list => list.created ?? '',
  }

const ContactsMailingLists = (): null | React.ReactNode => {
  const emailGroups = useSelector(state => state.emailGroups)
  const isAdmin = useSelector(getUser)?.is_admin
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const [sorting, setSorting] = useState<SortingState>(DEFAULT_SORTING)
  const [searchQuery, setSearchQuery] = useState('')
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const [isConfirmingBulkDelete, setIsConfirmingBulkDelete] = useState(false)
  const [isBulkDeleting, setIsBulkDeleting] = useState(false)

  const [modalOpen, setModalOpen] = useState(false)
  const [downloadUrl, setDownloadUrl] = useState(null)
  const [deferred, setDeferred] = useState(false)
  const [error, setError] = useState(false)
  const [loading, setLoading] = useState(false)
  const [exportId, setExportId] = useState<RecipientListId | null>(null)

  const exportClick = useCallback(
    async (id: RecipientListId) => {
      setLoading(true)
      setError(false)
      setDeferred(false)
      setDownloadUrl(null)
      setModalOpen(true)
      const result: Record<string, any> = await dispatch(exportEmailGroup(id))

      if (result?.deferred) {
        setDeferred(true)
        setLoading(false)
      }

      if (result?.download_url) {
        setDownloadUrl(result?.download_url)
        setLoading(false)
      }

      if (result?.error) {
        setError(true)
        setLoading(false)
      }
    },
    [dispatch],
  )

  const closeModal = () => setModalOpen(false)

  const renderModal = () => {
    if (!modalOpen) return null

    if (downloadUrl) {
      return (
        <DownloadModal type="base" onClose={closeModal} url={downloadUrl} />
      )
    }

    if (loading) {
      return (
        <DownloadModal type="loading" onClose={closeModal} url={downloadUrl} />
      )
    }

    if (error && exportId) {
      return (
        <DownloadModal
          type="error"
          onClose={closeModal}
          onClick={() => exportClick(exportId)}
        />
      )
    }

    if (deferred) {
      return <DownloadModal type="deferred" onClose={closeModal} />
    }
  }

  const trimmedQuery = searchQuery.trim()

  const visibleLists = useMemo(() => {
    const filtered = trimmedQuery
      ? emailGroups.mailingList.filter(list =>
          list.name?.toLowerCase().includes(trimmedQuery.toLowerCase()),
        )
      : emailGroups.mailingList

    const sort = sorting[0] ?? DEFAULT_SORTING[0]!
    const iteratee = SORT_ITERATEES[sort.id]
    if (!iteratee) return filtered
    return orderBy(filtered, [iteratee], [sort.desc ? 'desc' : 'asc'])
  }, [emailGroups.mailingList, trimmedQuery, sorting])

  // Rebuilt when the search changes so highlighted cells re-render (the
  // memoized table rows only re-render on column identity or value changes)
  const columns = useMemo<ColumnDef<MailingList>[]>(
    () => [
      {
        id: 'name',
        accessorKey: 'name',
        header: 'Name',
        cell: ({ row }) => (
          <Link
            className={styles.NameCellLink}
            to={`/lists/${row.original.id}`}
            translate="no"
          >
            {highlightMatch(row.original.name ?? '', trimmedQuery)}
          </Link>
        ),
      },
      {
        id: 'count_recipients',
        accessorFn: list => list.count_recipients ?? 0,
        header: 'Contacts',
        cell: ({ row }) => (
          <Link className={styles.CellLink} to={`/lists/${row.original.id}`}>
            {row.original.count_recipients ?? 0}
          </Link>
        ),
      },
      {
        id: 'created',
        accessorKey: 'created',
        header: 'Created',
        cell: ({ row }) => (
          <span
            title={dayjs(row.original.created).format('MMMM Do YYYY, h:mma')}
          >
            {dayjs(row.original.created).format('MMMM Do YYYY')}
          </span>
        ),
      },
    ],
    [trimmedQuery],
  )

  const getRowActions = useCallback(
    (list: MailingList): RowAction[] => [
      {
        label: 'Actions',
        icon: <LuEllipsis />,
        subActions: [
          {
            label: 'Edit list',
            icon: <LuPencil />,
            onClick: () => navigate(`/lists/${list.id}/edit`),
          },
          {},
          {
            label: CAPTION_EXISTING,
            icon: <LuUserPlus />,
            onClick: () => navigate(`/contacts/list?append=${list.id}`),
          },
          {
            label: CAPTION_IMPORT,
            icon: <LuFileUp />,
            onClick: () => navigate(`/lists/${list.id}/file-import`),
          },
          {
            label: CAPTION_SINGLE,
            icon: <LuUserRoundPlus />,
            onClick: () => navigate(`/lists/${list.id}/add-single`),
          },
          {},
          {
            label: isAdmin ? CAPTION_EXPORT : `${CAPTION_EXPORT} (admins only)`,
            icon: <LuDownload />,
            disabled: !isAdmin,
            onClick: () => {
              setExportId(list.id)
              exportClick(list.id)
            },
          },
          {},
          {
            label: 'Delete list',
            icon: <LuTrash2 />,
            color: 'red',
            onClick: () => navigate(`/lists/delete-list/${list.id}`),
          },
        ],
      },
    ],
    [isAdmin, navigate, exportClick],
  )

  const selectedLists = useMemo(
    () => emailGroups.mailingList.filter(list => rowSelection[list.id]),
    [emailGroups.mailingList, rowSelection],
  )

  const clearSelection = useCallback(() => setRowSelection({}), [])

  const handleFetchNextPage = useCallback(() => {
    dispatch(loadMailingListsNextPage())
  }, [dispatch])

  const handleConfirmBulkDelete = async () => {
    if (isBulkDeleting) return
    setIsBulkDeleting(true)
    try {
      const { deletedIds }: BulkDeleteResult = await dispatch(
        bulkDeleteEmailGroups(
          selectedLists.map(list => ({ id: list.id, name: list.name })),
        ),
      )
      // Only the rows that were actually deleted clear from the selection
      setRowSelection(previous => {
        const next = { ...previous }
        deletedIds.forEach(id => delete next[id])
        return next
      })
    } finally {
      setIsBulkDeleting(false)
      setIsConfirmingBulkDelete(false)
    }
  }

  const createNewListButton = (
    <Button
      appearance="primary"
      link="/lists/create"
      caption="Create new list"
    />
  )

  // Not-yet-fetched renders as loading too, so the first paint (before the
  // parent layout dispatches the load) shows skeletons instead of "no results"
  const isInitialLoading =
    emailGroups.isFetchingMailingLists || !emailGroups.mailingListsFetched

  const isEmpty =
    emailGroups.mailingListsFetched &&
    emailGroups.mailingList.length === 0 &&
    !emailGroups.isFetchingMailingLists

  const noResultsContent = trimmedQuery ? (
    <Flex p="9" direction="column" gap="2" align="center" justify="center">
      <Text weight="bold" color="gray">
        No lists match &ldquo;{trimmedQuery}&rdquo;
      </Text>
      <Text size="1" color="gray">
        Only the {emailGroups.mailingList.length} lists loaded so far were
        searched.
      </Text>
      {emailGroups.mailingListHasMore && (
        <DsButton
          size="1"
          variant="soft"
          loading={emailGroups.isFetchingNextMailingListsPage}
          onClick={handleFetchNextPage}
        >
          Load more lists
        </DsButton>
      )}
    </Flex>
  ) : undefined

  let content

  if (isEmpty) {
    content = (
      <div className={styles.EmptyMessage}>
        <EmptyMessage
          icon={<IconEmptyLists />}
          header="No mailing lists yet"
          cta={
            <div>
              Create your first mailing list by clicking the button below.
              <div className={styles.EmptyButtonContainer}>
                {createNewListButton}
              </div>
            </div>
          }
        />
      </div>
    )
  } else {
    content = (
      <Flex direction="column" gap="3" className={styles.TableSection}>
        <Flex justify="start">
          <SearchTextField
            value={searchQuery}
            onChange={event => setSearchQuery(event.target.value)}
            placeholder="Search lists..."
            className={styles.SearchField}
            aria-label="Search mailing lists"
          />
        </Flex>
        <div className={styles.TableWrapper}>
          <DataTable
            data={visibleLists}
            columns={columns}
            loading={isInitialLoading}
            sorting={sorting}
            onSortingChange={setSorting}
            rowSelection={rowSelection}
            onRowSelectionChange={setRowSelection}
            enableSelectAll
            getRowActions={getRowActions}
            hasNextPage={emailGroups.mailingListHasMore}
            isFetchingNextPage={emailGroups.isFetchingNextMailingListsPage}
            onFetchNextPage={handleFetchNextPage}
            hasNextPageError={emailGroups.mailingListNextPageError}
            onRetryNextPage={handleFetchNextPage}
            noResultsContent={noResultsContent}
          />
          {selectedLists.length >= 2 && (
            <SelectionActionMenu
              selectedCount={selectedLists.length}
              onDelete={() => setIsConfirmingBulkDelete(true)}
              onClear={clearSelection}
            />
          )}
        </div>
      </Flex>
    )
  }

  return (
    <>
      <DocumentHeadTags title="Mailing Lists | DISCO" />
      <PageHeader title="Mailing Lists" actions={createNewListButton} />
      <PageContainer>
        {content}
        <Outlet />
      </PageContainer>
      <AlertDialog.Root
        open={isConfirmingBulkDelete}
        onOpenChange={open => {
          if (!isBulkDeleting) setIsConfirmingBulkDelete(open)
        }}
      >
        <AlertDialog.Content maxWidth="400px" aria-describedby={undefined}>
          <AlertDialog.Title>
            Delete {selectedLists.length} lists
          </AlertDialog.Title>
          <Text as="p" size="2">
            {`Are you sure you want to permanently delete ${
              selectedLists.length === 1
                ? 'this list'
                : `these ${selectedLists.length} lists`
            }? This can’t be undone.`}
          </Text>
          <Flex gap="3" mt="4" justify="end">
            <AlertDialog.Cancel>
              <DsButton
                variant="soft"
                color="gray"
                type="button"
                disabled={isBulkDeleting}
              >
                Cancel
              </DsButton>
            </AlertDialog.Cancel>
            <DsButton
              color="red"
              type="button"
              loading={isBulkDeleting}
              onClick={handleConfirmBulkDelete}
            >
              Delete
            </DsButton>
          </Flex>
        </AlertDialog.Content>
      </AlertDialog.Root>
      {renderModal()}
    </>
  )
}

export default ContactsMailingLists
